import React, { useState } from "react";
import { useAutoPoll } from "./useAutoPoll";
import { backendUrl, authHeader } from "@/lib/seoApi";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { StatusPill, RefreshChip, LoadingState, ErrorState } from "./OpsDashboard";

const TESTABLE = ["mongo", "cms", "dataforseo", "textru", "smtp", "llm"];

export default function OpsHealth() {
  const { data, loading, error, lastUpdated, refresh } = useAutoPoll(
    backendUrl("/api/seo/ops/health"), { intervalMs: 15000 });
  const [testing, setTesting] = useState({});
  const [testResults, setTestResults] = useState({});

  const runTest = async (service) => {
    setTesting((t) => ({ ...t, [service]: true }));
    try {
      const r = await fetch(backendUrl(`/api/seo/ops/test/${service}`),
        { method: "POST", headers: authHeader() });
      const body = await r.json();
      setTestResults((t) => ({ ...t, [service]: body }));
    } catch (e) {
      setTestResults((t) => ({ ...t, [service]: { status: "red", detail: String(e) } }));
    } finally {
      setTesting((t) => ({ ...t, [service]: false }));
      refresh();
    }
  };

  if (error) return <ErrorState message={error} />;
  if (!data)  return <LoadingState />;

  return (
    <div className="p-6" data-testid="ops-health">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-neutral-900">System Health</h1>
          <p className="text-sm text-neutral-500 mt-1">Live status of every external and internal service.</p>
        </div>
        <div className="flex items-center gap-3">
          <StatusPill status={data.overall} />
          <RefreshChip lastUpdated={lastUpdated} loading={loading} />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(data.services || []).map((s) => {
          const slug = s.name.toLowerCase().replace(/\W+/g, "-");
          const testable = TESTABLE.find(k => slug.startsWith(k));
          const testResult = testResults[testable];
          const stats = s.stats_24h || {};
          return (
            <Card key={s.name} data-testid={`health-card-${slug}`}>
              <CardHeader className="flex-row items-center justify-between gap-2">
                <CardTitle className="text-base">{s.name}</CardTitle>
                <StatusPill status={s.status} />
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="text-sm text-neutral-700 min-h-[20px]">{s.detail || "—"}</div>
                <dl className="grid grid-cols-2 gap-2 text-xs">
                  <Row k="Response" v={s.response_ms != null ? `${s.response_ms} ms` : "—"} />
                  <Row k="Requests 24h" v={stats.requests_24h ?? 0} />
                  <Row k="Errors 24h" v={stats.errors_24h ?? 0}
                          tone={(stats.errors_24h ?? 0) > 0 ? "danger" : "neutral"} />
                  <Row k="Error rate" v={`${stats.error_rate_24h_pct ?? 0}%`} />
                  <Row k="Last success" v={fmtAt(stats.last_success_at)} />
                  <Row k="Last error" v={fmtAt(stats.last_error_at)} tone="danger" />
                </dl>
                {testable && (
                  <div className="flex items-center gap-2 pt-2 border-t border-neutral-100">
                    <Button size="sm" variant="outline"
                              onClick={() => runTest(testable)} disabled={testing[testable]}
                              data-testid={`test-btn-${slug}`}>
                      {testing[testable] ? "Testing…" : "Test connection"}
                    </Button>
                    {testResult && (
                      <span className="text-xs text-neutral-600">
                        <StatusPill status={testResult.status} /> · {testResult.detail}
                      </span>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

function Row({ k, v, tone }) {
  return (
    <>
      <dt className="text-neutral-500">{k}</dt>
      <dd className={`text-right tabular-nums ${tone === "danger" ? "text-red-700" : "text-neutral-800"}`}>{v ?? "—"}</dd>
    </>
  );
}

function fmtAt(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    const diff = Math.round((Date.now() - d.getTime()) / 1000);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
    return d.toISOString().slice(0, 10);
  } catch { return "—"; }
}
